import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Polls an async fetcher with resilience:
 *  - overlap guard: never two in-flight at once
 *  - AbortController: superseded/unmounted requests are aborted
 *  - keep-last-known-data on error: a failed fetch sets `error` but never clears `data`
 *  - refetch on focus: visibilitychange + pageshow (fixes bfcache / background-tab staleness)
 *  - guarded vs. forced: background ticks (timer/focus/mount) are dropped if a fetch is
 *    already in flight; an explicit `refetch()` while busy is coalesced into a single
 *    trailing re-run that fires once the in-flight fetch settles
 *  - durable failures: a 403 throttles the background poll to DENIED_RECHECK_MS instead of
 *    retrying every tick, while mount, focus and `refetch()` still re-check immediately.
 *    Throttling rather than stopping is required so an unattended display, which fires no
 *    focus events, still recovers once the grant is added.
 *
 * @param {(opts: { signal: AbortSignal }) => Promise<any>} fetcher
 * @param {number} intervalMs  polling cadence; falsy disables the interval (mount + focus + manual only)
 * @returns {{ data: any, error: any, isStale: boolean, isAccessDenied: boolean, isLoading: boolean, refetch: () => void, lastUpdatedAt: number | null }}
 *   `isAccessDenied` is true while the server refuses the request as unauthorized, and clears
 *   on the next success or on any other kind of failure.
 *   `lastUpdatedAt` is the epoch ms of the last successful fetch — null before the first
 *   success, and never set by failed or aborted fetches.
 */

// 401/403 keep failing until something changes server-side. Everything else (network, timeout,
// 5xx) is transient and keeps polling. A 401 is intercepted before it reaches this hook.
function isAuthorizationFailure(err) {
  const status = err && err.response && err.response.status;
  return status === 401 || status === 403;
}

// Long enough to stop the 10s poll hammering an endpoint that cannot succeed, short enough
// that an untouched screen recovers soon after the grant is added.
const DENIED_RECHECK_MS = 5 * 60 * 1000;
export default function usePolledResource(fetcher, intervalMs) {
  const [data, setData] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [error, setError] = useState(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const deniedRef = useRef(false);
  const deniedAtRef = useRef(0);
  const inFlightRef = useRef(false);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const pendingForceRef = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Ref indirection so the trailing re-run in `finally` can invoke runFetch without
  // listing it in its own useCallback([]) deps (avoids react-hooks/exhaustive-deps).
  const runFetchRef = useRef(null);

  const runFetch = useCallback(async (opts) => {
    const force = !!(opts && opts.force);
    const background = !!(opts && opts.background);
    // Only timer ticks are throttled; mount, focus and refetch always re-check immediately.
    if (background && deniedRef.current && Date.now() - deniedAtRef.current < DENIED_RECHECK_MS) {
      return;
    }
    if (inFlightRef.current) {
      if (force) pendingForceRef.current = true; // coalesce one trailing explicit refetch
      return; // background ticks are simply dropped
    }
    inFlightRef.current = true;
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await fetcherRef.current({ signal: controller.signal });
      if (!mountedRef.current || controller.signal.aborted) return;
      setData(result);
      setLastUpdatedAt(Date.now());
      setError(null);
      if (deniedRef.current) {
        deniedRef.current = false;
        setIsAccessDenied(false);
      }
    } catch (err) {
      if (controller.signal.aborted || (err && (err.name === 'CanceledError' || err.name === 'AbortError'))) {
        return; // cancellation is not an error state
      }
      if (!mountedRef.current) return;
      setError(err); // keep last-known data; only flag the error
      if (isAuthorizationFailure(err)) {
        deniedRef.current = true;
        deniedAtRef.current = Date.now();
        setIsAccessDenied(true);
      } else if (deniedRef.current) {
        // Without this the flag would latch, blaming permissions for every later failure.
        deniedRef.current = false;
        setIsAccessDenied(false);
      }
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) {
        setIsLoading(false);
        if (pendingForceRef.current) {
          pendingForceRef.current = false;
          runFetchRef.current({ force: true }); // trailing re-run for explicit refetch(es) dropped while busy
        }
      }
    }
  }, []);
  runFetchRef.current = runFetch;

  useEffect(() => {
    mountedRef.current = true;
    runFetch();

    let id = null;
    if (intervalMs) {
      // Wrapped so the tick is flagged as background, and so setInterval does not pass the
      // timer id in as the options argument.
      id = setInterval(() => runFetch({ background: true }), intervalMs);
    }

    const onFocus = () => {
      if (document.visibilityState === 'visible') runFetch();
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('pageshow', onFocus);

    return () => {
      mountedRef.current = false;
      if (id) clearInterval(id);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('pageshow', onFocus);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [runFetch, intervalMs]);

  const refetch = useCallback(() => runFetch({ force: true }), [runFetch]);

  return { data, error, isStale: error != null, isAccessDenied, isLoading, refetch, lastUpdatedAt };
}
